/**
 * DraggableCardGrid - drag-to-reorder detail cards, using the EXACT pointer-based
 * mechanism the dashboard uses for its tiles (a ghost of the grabbed card follows
 * the cursor, rect hit-testing decides the drop, edge auto-scroll, an accent drop
 * ring on the target, the source dims). The one deliberate difference from the
 * dashboard is that these cards are NOT resizable - there is no resize handle and
 * no per-card span.
 *
 * Persistence mirrors the dashboard exactly: the order is cached in localStorage
 * (tenant-scoped, read synchronously for an instant, glitch-free first paint) AND
 * saved to the user's server-backed profile via useUserPreference (so it follows
 * the user across devices). On mount the localStorage value paints immediately;
 * once the server value has loaded it reconciles (server is the source of truth)
 * and refreshes the cache.
 *
 * The card set is conditional (cards appear and disappear per record), so the
 * effective order is the saved order filtered to the cards currently present,
 * with any card it has never seen placed after the card it follows by default.
 *
 * Two ways to supply the cards:
 *  - `cards`: an explicit [{ key, title, node }] list (clean keys, nice ghost
 *    labels) - used where the JSX is easy to restructure.
 *  - `children`: raw JSX children - each top-level card child MUST carry a stable
 *    `key`; the ghost label is taken from the child's `title` prop when present.
 *
 * SECTIONS (children mode): some layouts group cards under full-width section
 * headings. Mark a heading child with `data-reorder-divider` and a non-card child
 * (e.g. an invisible dialog) with `data-reorder-static`. Dividers render full
 * width, are never draggable, and split the cards into bands - a card only ever
 * reorders WITHIN its own band, never across a heading. Statics render inline and
 * are never draggable. With no dividers the whole grid is a single band, so the
 * simple (registration / claim overview) usages are unaffected.
 */

import {
  Children,
  Fragment,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useUserPreference } from "@/admin/hooks/use-user-preference";
import { computeBandedOrder } from "./card-order";
import { CardDragHandleProvider } from "@/shared/components/card-drag-handle";

export type ReorderableCard = {
  /** Stable key - drives the persisted order. Must be stable across renders. */
  key: string;
  /** Human label shown on the drag ghost. */
  title: string;
  /** The card to render (a DetailCard, panel, etc.). */
  node: ReactNode;
};

type GridItem =
  | {
      kind: "card";
      key: string;
      title: string;
      node: ReactNode;
      section: number;
    }
  | {
      kind: "divider";
      id: string;
      node: ReactNode;
      /** The band this heading opens - used to suppress a heading whose cards are all hidden. */
      section: number;
    }
  | { kind: "static"; id: string; node: ReactNode };

function tenantSlug(): string {
  try {
    return sessionStorage.getItem("tenantSlug") ?? "tenant";
  } catch {
    return "tenant";
  }
}

function lsKey(storageKey: string): string {
  return `raven:cards:${storageKey}:${tenantSlug()}`;
}

function readLsOrder(storageKey: string): string[] | null {
  try {
    const raw = localStorage.getItem(lsKey(storageKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : null;
  } catch {
    return null;
  }
}

function writeLsOrder(storageKey: string, order: string[]): void {
  try {
    localStorage.setItem(lsKey(storageKey), JSON.stringify(order));
  } catch {
    /* private mode / quota - the server copy still persists */
  }
}

/**
 * WPS-166: the user's hide/restore overrides, stored as two explicit sets.
 *
 * Not a single "hidden" list, because there are TWO layers. The surface supplies
 * `defaultHiddenKeys` - cards an admin has curated off this workflow's Overview,
 * so nobody has to hide them one by one - and the user may then hide more cards
 * OR restore one of the defaults. Recording both directions is what lets an
 * admin later change the default and have that reach every user who never had
 * an opinion about that card, instead of freezing today's defaults into
 * everyone's saved copy.
 *
 * Effective hidden = (defaults + user hides) - user restores.
 */
export type CardHideOverrides = { hide: string[]; show: string[] };

const EMPTY_OVERRIDES: CardHideOverrides = { hide: [], show: [] };

function hiddenKey(storageKey: string, scope?: string): string {
  return scope ? `${storageKey}:hidden:${scope}` : `${storageKey}:hidden`;
}

function normaliseOverrides(raw: unknown): CardHideOverrides | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Partial<Record<keyof CardHideOverrides, unknown>>;
  const list = (v: unknown) =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  return { hide: list(value.hide), show: list(value.show) };
}

function readLsOverrides(prefKey: string): CardHideOverrides | null {
  try {
    const raw = localStorage.getItem(lsKey(prefKey));
    return raw ? normaliseOverrides(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeLsOverrides(prefKey: string, overrides: CardHideOverrides): void {
  try {
    localStorage.setItem(lsKey(prefKey), JSON.stringify(overrides));
  } catch {
    /* private mode / quota - the server copy still persists */
  }
}

/**
 * First-paint overrides for a scope. A scope with nothing stored yet inherits
 * the hides saved before overrides were scoped, but not the restores: those
 * undid some other workflow's defaults.
 */
function initialOverrides(
  storageKey: string,
  scope?: string,
): CardHideOverrides {
  const own = readLsOverrides(hiddenKey(storageKey, scope));
  if (own || !scope) return own ?? EMPTY_OVERRIDES;
  const legacy = readLsOverrides(hiddenKey(storageKey));
  return legacy ? { hide: legacy.hide, show: [] } : EMPTY_OVERRIDES;
}

/**
 * WPS-166: deal each band's cards into columns, shortest column first.
 *
 * CSS multi-column (what this grid used) balances by SPLITTING content to even
 * the columns out, which it cannot do when every card carries
 * `break-inside-avoid` - so one column runs long and the other stops early,
 * leaving a band of white space above the next section heading. Measured on a
 * real claim: the People & Property band ran 948px against 483px, wasting
 * 513px. Dealing each card to whichever column is currently shortest splits the
 * same cards ~715/715.
 *
 * Heights come from the previous paint. A card not yet measured is assumed
 * average rather than zero, so the very first deal is sensible rather than
 * stacking everything into column one.
 */
function packIntoColumns(
  keys: string[],
  heights: Record<string, number>,
  columnCount: number,
  /**
   * WPS-166: cards that must LEAD a specific column, whatever the packer would
   * otherwise decide (card key -> zero-based column). The Workmanship Overview
   * needs Product at the head of the right column, which packing by height
   * alone can never guarantee. Everything not pinned is dealt around them, so
   * the rest of the band still packs tight.
   */
  pinnedColumns?: Record<string, number>,
): string[][] {
  // Never open more columns than there are cards to fill them. A band left
  // with a single card (because the rest are hidden) would otherwise render it
  // in column one against an empty column two - exactly the dead space this
  // function exists to remove.
  columnCount = Math.min(columnCount, keys.length);
  if (columnCount <= 1) return [keys];
  const measured = keys.map((k) => heights[k]).filter((h) => typeof h === "number");
  const fallback = measured.length
    ? measured.reduce((a, b) => a + b, 0) / measured.length
    : 240;
  const columns = Array.from({ length: columnCount }, () => ({
    height: 0,
    keys: [] as string[],
  }));
  // Seat the pinned cards first, in their declared column, so they lead it.
  const pinned = new Set<string>();
  for (const key of keys) {
    const column = pinnedColumns?.[key];
    if (column === undefined) continue;
    const seat = columns[Math.min(Math.max(column, 0), columnCount - 1)];
    seat.keys.push(key);
    seat.height += heights[key] ?? fallback;
    pinned.add(key);
  }
  const rest = keys.filter((k) => !pinned.has(k));
  const heightOf = (k: string) => heights[k] ?? fallback;

  if (columnCount === 2) {
    // Two columns is every real caller, and there it is worth doing properly.
    // Dealing card-by-card to the shortest column is order-dependent and gets
    // this wrong: on a band of 300 / 150 / 380 it produces 300 against 530,
    // because the second card lands in the empty column and the third then
    // follows it. Trying every order-preserving split instead finds 450
    // against 380 - and the split is cheap to evaluate, being one pass.
    let bestIndex = 0;
    let bestGap = Infinity;
    let left = columns[0].height;
    const totalRest = rest.reduce((sum, k) => sum + heightOf(k), 0);
    for (let i = 0; i <= rest.length; i++) {
      if (i > 0) left += heightOf(rest[i - 1]);
      const right =
        columns[1].height + totalRest - (left - columns[0].height);
      const gap = Math.abs(left - right);
      if (gap < bestGap) {
        bestGap = gap;
        bestIndex = i;
      }
    }
    columns[0].keys.push(...rest.slice(0, bestIndex));
    columns[1].keys.push(...rest.slice(bestIndex));
    return columns.map((c) => c.keys);
  }

  for (const key of rest) {
    // Ties go to the leftmost column, so an unmeasured first paint still deals
    // left-to-right in the user's saved order.
    let target = columns[0];
    for (const column of columns) {
      if (column.height < target.height) target = column;
    }
    target.keys.push(key);
    target.height += heightOf(key);
  }
  return columns.map((c) => c.keys);
}

/**
 * Normalise raw children into grid items (cards, dividers, statics + bands).
 *
 * Fragments are flattened into their children so a `{cond && <>A B</>}` grouping
 * yields TWO independent, individually-draggable cards (each with its own spacing)
 * rather than one wrapper holding both. React.Children does NOT recurse into a
 * Fragment on its own, so we walk it explicitly. Only real Fragments are
 * flattened - a plain wrapper `<div>` (e.g. a deliberately-paired flex column) is
 * kept as a single card, so intentional groupings still move as one unit.
 */
function childrenToItems(
  children: ReactNode,
  fallbackTitle: string,
): GridItem[] {
  const items: GridItem[] = [];
  const ctx = { section: 0, counter: 0 };
  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (child) => {
      if (
        child == null ||
        typeof child === "boolean" ||
        typeof child === "string" ||
        typeof child === "number"
      ) {
        return;
      }
      if (!isValidElement(child)) return;
      const el = child as ReactElement;
      if (el.type === Fragment) {
        walk((el.props as { children?: ReactNode }).children);
        return;
      }
      const props = (el.props ?? {}) as Record<string, unknown>;
      const id = `__item_${ctx.counter++}`;
      if (props["data-reorder-divider"]) {
        // A divider opens a new band. The first divider promotes the cards that
        // follow it into the next band (cards before any divider stay in band 0).
        if (items.some((it) => it.kind === "card" || it.kind === "divider"))
          ctx.section += 1;
        // The divider carries the section it opens so a heading whose cards the
        // user has all hidden can be suppressed too - a lone "Coverage" rule
        // above nothing reads as a loading failure (WPS-166).
        items.push({ kind: "divider", id, node: el, section: ctx.section });
        return;
      }
      if (props["data-reorder-static"]) {
        items.push({ kind: "static", id, node: el });
        return;
      }
      // A card's label comes from its own `title` prop where it has one. Some
      // cards are self-titling components with no such prop (Notes, Claim Team,
      // Communications ...), which used to be harmless - the label only showed
      // on the drag ghost. WPS-166 puts it in the hide control and the restore
      // bar, where five cards all reading "Card" is unusable, so those call
      // sites declare `data-card-title`. Generic escape hatch, no card is named
      // here.
      const declaredTitle = props["data-card-title"];
      const title =
        typeof props.title === "string"
          ? props.title
          : typeof declaredTitle === "string" && declaredTitle
            ? declaredTitle
            : fallbackTitle;
      items.push({
        kind: "card",
        key: String(el.key),
        title,
        node: el,
        section: ctx.section,
      });
    });
  };
  walk(children);
  return items;
}

export function DraggableCardGrid({
  storageKey,
  cards,
  children,
  className,
  itemClassName,
  locked = false,
  hideable = false,
  defaultHiddenKeys,
  hiddenScope,
  packed = false,
  packedColumns = 2,
  pinnedColumns,
}: {
  storageKey: string;
  /** Pinned: the arrangement is held still. Dragging is off and the grips are
   *  hidden, so a saved layout cannot be nudged out of shape by a stray drag. */
  locked?: boolean;
  /** Explicit card list. Provide this OR `children`. */
  cards?: ReorderableCard[];
  /** Raw children (each top-level card child needs a stable `key`). Provide this OR `cards`. */
  children?: ReactNode;
  /** Layout classes for each band's card container (a CSS grid or masonry columns). */
  className?: string;
  /** Classes applied to every card wrapper (e.g. masonry break-inside rules). */
  itemClassName?: string;
  /** Let the user hide cards and restore them. Opt-in per surface. */
  hideable?: boolean;
  /**
   * Cards hidden by default for everyone on this surface - an admin-curated set
   * (e.g. a workflow's ``overview_hidden_cards``). The user can restore any of
   * them. This is a DEFAULT, never a permission: anything a viewer must not see
   * has to be withheld by the caller, not hidden here.
   */
  defaultHiddenKeys?: string[];
  /**
   * Keeps the hide/restore choices apart per scope (e.g. per workflow), since
   * `defaultHiddenKeys` differ between them. The card order stays shared.
   */
  hiddenScope?: string;
  /**
   * Deal each band's cards into balanced columns rather than leaving it to CSS
   * multi-column, which cannot split a `break-inside-avoid` card and so leaves
   * one column short. Opt-in per surface: it changes how a band lays out, and
   * `className` is not applied to a packed band (the columns own their layout).
   */
  packed?: boolean;
  /** Columns to deal into on a wide viewport. Ignored unless `packed`. */
  packedColumns?: number;
  /**
   * Cards that must LEAD a given column (card key -> zero-based column index),
   * regardless of what packing by height would choose. Ignored unless `packed`.
   */
  pinnedColumns?: Record<string, number>;
}) {
  const { t } = useTranslation();
  const fallbackTitle = t("detailCards.fallbackTitle", "Card");
  const items = useMemo<GridItem[]>(
    () =>
      cards
        ? cards.map((c) => ({
            kind: "card",
            key: c.key,
            title: c.title,
            node: c.node,
            section: 0,
          }))
        : childrenToItems(children, fallbackTitle),
    [cards, children, fallbackTitle],
  );

  const orderPref = useUserPreference<string[] | null>(storageKey, null);

  // The saved order, seeded synchronously from localStorage so the first paint is
  // already in the user's arrangement (no flash of the default order).
  const [savedOrder, setSavedOrder] = useState<string[] | null>(() =>
    readLsOrder(storageKey),
  );
  // A new key is a different saved layout: re-seed rather than keep the old one.
  const [orderFor, setOrderFor] = useState(storageKey);
  if (orderFor !== storageKey) {
    setOrderFor(storageKey);
    setSavedOrder(readLsOrder(storageKey));
  }

  // Once the server value has loaded, adopt it as the source of truth (a different
  // device may have changed it) and refresh the local cache. Once per key.
  const serverHydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!orderPref.isLoaded || serverHydratedFor.current === storageKey) return;
    serverHydratedFor.current = storageKey;
    const server = orderPref.value;
    if (server && server.length) {
      setSavedOrder(server);
      writeLsOrder(storageKey, server);
    }
  }, [orderPref.isLoaded, orderPref.value, storageKey]);

  // ---- Hide / restore (WPS-166) ------------------------------------------
  const overridesKey = hiddenKey(storageKey, hiddenScope);
  const hiddenPref = useUserPreference<CardHideOverrides | null>(
    overridesKey,
    null,
  );
  const [overrides, setOverrides] = useState<CardHideOverrides>(() =>
    initialOverrides(storageKey, hiddenScope),
  );
  const [overridesFor, setOverridesFor] = useState(overridesKey);
  if (overridesFor !== overridesKey) {
    setOverridesFor(overridesKey);
    setOverrides(initialOverrides(storageKey, hiddenScope));
  }
  const hiddenHydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!hiddenPref.isLoaded || hiddenHydratedFor.current === overridesKey)
      return;
    hiddenHydratedFor.current = overridesKey;
    const server = normaliseOverrides(hiddenPref.value);
    if (server) {
      setOverrides(server);
      writeLsOverrides(overridesKey, server);
    }
  }, [hiddenPref.isLoaded, hiddenPref.value, overridesKey]);

  const defaultHiddenSignature = (defaultHiddenKeys ?? []).join("|");
  const hiddenKeys = useMemo(() => {
    if (!hideable) return new Set<string>();
    const set = new Set(defaultHiddenKeys ?? []);
    overrides.hide.forEach((k) => set.add(k));
    overrides.show.forEach((k) => set.delete(k));
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideable, defaultHiddenSignature, overrides]);

  const commitOverrides = useCallback(
    (next: CardHideOverrides) => {
      setOverrides(next);
      writeLsOverrides(overridesKey, next);
      hiddenPref.setValue(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overridesKey],
  );

  // `hideCard`'s Undo needs `restoreCard`, which is declared below it. A ref
  // breaks the cycle without making either callback depend on the other's
  // identity (which would rebuild every card's hide handler on each change).
  const restoreCardRef = useRef<(key: string) => void>(() => {});

  const hideCard = useCallback(
    (key: string, title: string) => {
      const isDefaultHidden = (defaultHiddenKeys ?? []).includes(key);
      commitOverrides({
        // Hiding a card the admin already hides by default is just dropping the
        // user's restore, not adding a redundant hide.
        hide: isDefaultHidden
          ? overrides.hide.filter((k) => k !== key)
          : Array.from(new Set([...overrides.hide, key])),
        show: overrides.show.filter((k) => k !== key),
      });
      // Undo on the toast, because the commonest hide is an accidental one and
      // the restore bar at the foot of the grid is a scroll away.
      toast.success(t("detailCards.cardHidden", "{{title}} hidden", { title }), {
        action: {
          label: t("common.undo", "Undo"),
          onClick: () => restoreCardRef.current(key),
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commitOverrides, defaultHiddenSignature, overrides, t],
  );

  const restoreCard = useCallback(
    (key: string) => {
      const isDefaultHidden = (defaultHiddenKeys ?? []).includes(key);
      commitOverrides({
        hide: overrides.hide.filter((k) => k !== key),
        // Only a card hidden by DEFAULT needs an explicit restore recorded;
        // otherwise dropping it from `hide` is enough, and leaving it out keeps
        // the stored overrides minimal.
        show: isDefaultHidden
          ? Array.from(new Set([...overrides.show, key]))
          : overrides.show.filter((k) => k !== key),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commitOverrides, defaultHiddenSignature, overrides],
  );

  useEffect(() => {
    restoreCardRef.current = restoreCard;
  }, [restoreCard]);

  const restoreAll = useCallback(() => {
    commitOverrides({ hide: [], show: [...(defaultHiddenKeys ?? [])] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitOverrides, defaultHiddenSignature]);

  // Effective order, computed per band: saved order (present cards of that band
  // only), new cards slotted in after their default neighbour. Bands never mix,
  // so a card can only move within its own section. The result is a flat key list
  // in render order (band 0 cards, then band 1 cards, ...), contiguous per band.
  //
  // Hidden cards are removed BEFORE the order is computed, so a hidden card
  // leaves no gap and never becomes a drop target - but its position in
  // `savedOrder` is untouched, so restoring it puts it back where it was rather
  // than at the end.
  // WPS-146: the order the grid is CURRENTLY showing while a drag is in flight.
  // The drop used to be the first time anything moved - you carried a ghost
  // around over a static page and only found out where the card would land when
  // you let go. Previewing the real order means the cards open a gap under the
  // cursor as you go, so the drop holds no surprise. Null when not dragging, so
  // the saved order is the single source of truth at rest.
  const [previewOrder, setPreviewOrder] = useState<string[] | null>(null);
  const effectiveOrder = previewOrder ?? savedOrder;

  const { orderedKeys, cardByKey, bands, hiddenCards } = useMemo(() => {
    const allCardItems = items.filter(
      (it): it is Extract<GridItem, { kind: "card" }> => it.kind === "card",
    );
    const byKey = new Map(allCardItems.map((c) => [c.key, c]));
    const cardItems = allCardItems.filter((c) => !hiddenKeys.has(c.key));
    const hidden = allCardItems.filter((c) => hiddenKeys.has(c.key));
    const { flat, bands: perBand } = computeBandedOrder(
      cardItems.map((c) => ({ key: c.key, section: c.section })),
      effectiveOrder ?? [],
    );
    return {
      orderedKeys: flat,
      cardByKey: byKey,
      bands: perBand,
      hiddenCards: hidden,
    };
  }, [items, effectiveOrder, hiddenKeys]);

  // A ref mirror of the live key order, read by the drag handler (which is set up
  // once and must always see the latest order).
  const orderRef = useRef<string[]>([]);
  useEffect(() => {
    orderRef.current = orderedKeys;
  }, [orderedKeys]);

  const commitOrder = useCallback(
    (next: string[]) => {
      setSavedOrder(next); // local state (instant re-render)
      writeLsOrder(storageKey, next); // localStorage cache (sync, glitch-free)
      orderPref.setValue(next); // server profile (optimistic)
      toast.success(t("detailCards.layoutUpdated", "Layout updated"));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey],
  );

  const gridRef = useRef<HTMLDivElement | null>(null);

  // ---- Packed columns (WPS-166) ------------------------------------------
  // Card heights from the last paint, feeding the shortest-column deal. Only
  // measured when `packed` is on, so the CSS-column callers pay nothing.
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({});
  // Below `md` the grid is a single column, so packing must not reorder.
  const [isWide, setIsWide] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  useEffect(() => {
    if (!packed || typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsWide(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [packed]);

  useLayoutEffect(() => {
    if (!packed) return;
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      if (draggingRef.current) return;
      const next: Record<string, number> = {};
      grid.querySelectorAll<HTMLElement>("[data-tile-id]").forEach((el) => {
        const key = el.getAttribute("data-tile-id");
        if (key) next[key] = el.getBoundingClientRect().height;
      });
      setCardHeights((prev) => {
        // Only re-deal on a real change, and ignore sub-pixel noise - writing
        // state on every measurement would loop forever, because re-dealing
        // changes the heights it just measured.
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length === nextKeys.length) {
          let same = true;
          for (const k of nextKeys) {
            if (Math.abs((prev[k] ?? 0) - next[k]) > 1) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return next;
      });
    };
    measure();
    // Cards grow and shrink in place (a "Show 2 more" expander, the Notes
    // composer opening), so re-deal when any of them changes size.
    const observer = new ResizeObserver(measure);
    grid.querySelectorAll<HTMLElement>("[data-tile-id]").forEach((el) => {
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [packed, orderedKeys, hiddenKeys, isWide]);
  // ---- FLIP: cards glide to their new slot instead of teleporting ----------
  // Every reorder - a live preview step, a drop, a card being restored - is a
  // plain re-render, so without this the whole grid jumps between arrangements
  // and the eye has to re-find each card. Measure where every tile WAS, let the
  // browser lay out where it now IS, then play it back from the old offset.
  //
  // Driven by the Web Animations API rather than by writing `style.transform`
  // and clearing it on a later frame. That is not a stylistic choice: the
  // hand-rolled version sets a transform NOW and removes it in a callback, so
  // anything that stops the callback running - a background tab, a hidden
  // pane, a throttled frame budget - leaves the card stranded at the offset,
  // permanently and metres off screen. An animation owns its own value and
  // never writes to the element, so the worst a throttled tab can do is skip
  // the movement and show the cards where they belong.
  const prevRectsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    const next = new Map<string, { x: number; y: number }>();
    for (const el of grid.querySelectorAll<HTMLElement>("[data-tile-id]")) {
      const k = el.getAttribute("data-tile-id");
      if (!k) continue;
      const r = el.getBoundingClientRect();
      // Page-relative, so scrolling between two renders does not read as every
      // card having moved at once.
      const pos = { x: r.left + window.scrollX, y: r.top + window.scrollY };
      next.set(k, pos);
      const was = prevRectsRef.current.get(k);
      if (!was || reduced || typeof el.animate !== "function") continue;
      const dx = was.x - pos.x;
      const dy = was.y - pos.y;
      // Sub-pixel drift is layout noise, not movement. The upper bound is the
      // other half of the same judgement: a card does not travel a screen and
      // a half to change places with its neighbour, so a delta that large is a
      // remount or a band appearing, and animating it would be a card flying
      // in from off-screen rather than a reorder.
      const travelled = Math.abs(dx) + Math.abs(dy);
      if (travelled < 1 || travelled > 2000) continue;
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
        { duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" },
      );
    }
    prevRectsRef.current = next;
  });

  const dragRef = useRef<{ key: string; order: string[] | null } | null>(null);
  // True for the length of a drag. The height measurement below re-deals the
  // columns whenever a card changes size, and mid-drag every preview reorder
  // changes sizes - so measuring during a drag feeds the packer its own output
  // and the layout hunts. Heights are frozen at the values from the last settled
  // paint and released on drop.
  const draggingRef = useRef(false);
  // Tears down the in-flight drag (document listeners, ghost, body style, RAF).
  // Held in a ref so an unmount MID-DRAG can run it - otherwise the document
  // pointermove/pointerup listeners would leak past the component's life.
  const dragCleanupRef = useRef<(() => void) | null>(null);

  // Run any in-flight drag teardown when the component unmounts.
  useEffect(() => () => dragCleanupRef.current?.(), []);

  // ---- Drag reorder (pointer-based, rect hit-testing; works on touch too) ----
  // Ported from the dashboard tile grid, minus resize, plus band containment: a
  // card only ever reorders among cards in its own `data-section`.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    // Pinned: never attach the listener at all, rather than starting a drag and
    // discarding it. A card that lifts under the cursor and snaps back reads as
    // broken; one that does not move reads as held.
    if (locked) return;
    function onDown(e: PointerEvent) {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest("button, a, input, select, textarea")) return;
      const tileEl = target.closest("[data-tile-id]") as HTMLElement | null;
      if (!tileEl) return;
      const key = tileEl.getAttribute("data-tile-id");
      if (!key) return;
      const band = tileEl.getAttribute("data-section") ?? "0";
      const startX = e.clientX;
      const startY = e.clientY;
      let started = false;
      // A ghost of the dragged card - the exact size of the source tile - lifted
      // from where it was grabbed and following the cursor, so the drag reads as
      // "I'm carrying this card", not just a dimmed source + a drop ring.
      let ghost: HTMLElement | null = null;
      const startRect = tileEl.getBoundingClientRect();
      const startOrder = orderRef.current.join("|");
      const grabX = startX - startRect.left;
      const grabY = startY - startRect.top;
      dragRef.current = { key, order: null };

      let lastX = startX;
      let lastY = startY;
      let rafId = 0;

      // The cards this drag may reorder among: only the ones in the same band.
      const bandCards = () => [
        ...grid!.querySelectorAll<HTMLElement>(
          `[data-tile-id][data-section="${band}"]`,
        ),
      ];

      function clearTargets() {
        grid!
          .querySelectorAll('[data-drop-target="1"]')
          .forEach((n) => n.removeAttribute("data-drop-target"));
      }
      function positionGhost() {
        if (ghost)
          ghost.style.transform = `translate(${lastX - grabX}px, ${lastY - grabY}px)`;
      }
      // Work out the resulting order from the current pointer position (within the
      // dragged card's band), and store it so the drop just applies it.
      function computeDrop() {
        const prev = orderRef.current;
        const o = prev.slice();
        const from = o.indexOf(key!);
        let markerEl: Element | null = null;
        if (from > -1) {
          o.splice(from, 1);
          // WPS-166: measure the band ONCE per call. This used to read every
          // card's rect for the hit-test and then, on the empty-space path,
          // re-query the DOM and read every rect again - two forced layouts per
          // card, on every pointermove. That is what made a drag feel like it
          // was catching rather than gliding. One query + one rect per card now,
          // shared by both paths.
          const measured = bandCards().map((n) => ({
            el: n,
            k: n.getAttribute("data-tile-id"),
            r: n.getBoundingClientRect(),
          }));
          // WPS-146: with the order previewed live, the dragged card now
          // occupies the gap it opened. Once the cursor is inside that gap
          // there is no new decision to make - and re-deciding is what makes a
          // live-reordering grid flicker between two arrangements a pixel
          // apart. Hold the arrangement while the cursor is over our own slot.
          const own = measured.find((m) => m.k === key);
          if (
            own &&
            lastX >= own.r.left &&
            lastX <= own.r.right &&
            lastY >= own.r.top &&
            lastY <= own.r.bottom
          ) {
            return;
          }
          let overEl: HTMLElement | null = null;
          let overRect: DOMRect | null = null;
          for (const m of measured) {
            if (!m.k || m.k === key) continue;
            if (
              lastX >= m.r.left &&
              lastX <= m.r.right &&
              lastY >= m.r.top &&
              lastY <= m.r.bottom
            ) {
              overEl = m.el;
              overRect = m.r;
            }
          }
          let insertAt: number;
          if (overEl && overRect) {
            const overKey = (overEl as HTMLElement).getAttribute(
              "data-tile-id",
            )!;
            // Before or after, decided by the card's own midline rather than by
            // merely touching it: dropping "onto" a card is ambiguous, and
            // taking its index outright means the two cards trade places again
            // the moment the cursor drifts back a pixel. Indexed in `o` (the
            // order without the dragged card), where the insert happens.
            const after = lastY > overRect.top + overRect.height / 2;
            insertAt = o.indexOf(overKey) + (after ? 1 : 0);
            markerEl = overEl;
          } else {
            // Empty space within the band: a row-aware insertion index among the
            // band's cards, clamped so the card never leaves its band.
            const byKey = new Map(
              measured
                .filter((m): m is typeof m & { k: string } => !!m.k)
                .map((m) => [m.k, m]),
            );
            const others = o
              .map((k) => byKey.get(k))
              .filter((m): m is NonNullable<typeof m> => !!m);
            let rel = others.length;
            for (let i = 0; i < others.length; i++) {
              const r = others[i].r;
              if (lastY < r.top) {
                rel = i;
                break;
              }
              if (lastY <= r.bottom && lastX < r.left + r.width / 2) {
                rel = i;
                break;
              }
            }
            const anchor = others[rel] ?? others[others.length - 1] ?? null;
            markerEl = anchor?.el ?? null;
            insertAt = anchor ? o.indexOf(anchor.k) : from;
            if (anchor && rel >= others.length)
              insertAt = o.indexOf(anchor.k) + 1;
          }
          o.splice(Math.max(0, Math.min(insertAt, o.length)), 0, key!);
        }
        clearTargets();
        markerEl?.setAttribute("data-drop-target", "1");
        const changed = o.join("|") !== prev.join("|");
        // Show it. `prev` is the order on screen (the preview once one is in
        // flight), so each move is a small step from what the user can see
        // rather than a jump from where the drag began. A frame with nothing
        // new to move keeps the preview: the drop lands what is on screen.
        if (changed) {
          dragRef.current = {
            key: key!,
            order: o.join("|") === startOrder ? null : o,
          };
          orderRef.current = o;
          setPreviewOrder(o);
        }
      }
      // WPS-166: set by pointermove, consumed by the animation frame. A pointer
      // can fire several moves between two frames (more on a 120Hz trackpad),
      // and the old code did the full rect sweep on every one of them. Coalescing
      // to one sweep per painted frame is what turns the drag from catchy into
      // smooth; nothing is lost, because only the last position of a frame can
      // be seen anyway.
      let needsCompute = false;
      function frame() {
        rafId = requestAnimationFrame(frame);
        if (!started) return;
        const EDGE = 70;
        const MAX = 22;
        const vh = window.innerHeight;
        let dy = 0;
        if (lastY < EDGE) dy = -Math.ceil(((EDGE - lastY) / EDGE) * MAX);
        else if (lastY > vh - EDGE)
          dy = Math.ceil(((lastY - (vh - EDGE)) / EDGE) * MAX);
        if (dy !== 0) {
          window.scrollBy(0, dy);
          // Scrolling moves every card under a stationary cursor, so the drop
          // target must be recomputed even when the pointer itself has not moved.
          needsCompute = true;
        }
        if (!needsCompute) return;
        needsCompute = false;
        positionGhost();
        computeDrop();
      }
      function onMove(ev: PointerEvent) {
        lastX = ev.clientX;
        lastY = ev.clientY;
        if (!started) {
          if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 6)
            return;
          started = true;
          draggingRef.current = true;
          tileEl!.setAttribute("data-dragging", "1");
          document.body.style.userSelect = "none";
          ghost = document.createElement("div");
          ghost.className = "cv-drag-ghost";
          ghost.style.width = `${startRect.width}px`;
          ghost.style.height = `${startRect.height}px`;
          const label = document.createElement("span");
          label.className = "cv-drag-ghost-title";
          label.textContent = tileEl!.getAttribute("data-tile-title") || "Card";
          ghost.appendChild(label);
          document.body.appendChild(ghost);
          // Paint the ghost where the card was grabbed before the first frame,
          // so it appears under the cursor rather than jumping in from the
          // off-screen parking transform.
          positionGhost();
          rafId = requestAnimationFrame(frame);
        }
        needsCompute = true;
      }
      // Tear down everything this drag attached to the document / body / DOM.
      // Shared by the normal pointerup AND the unmount-mid-drag path so the
      // listeners can never outlive the drag.
      function cleanup() {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        cancelAnimationFrame(rafId);
        draggingRef.current = false;
        document.body.style.userSelect = "";
        tileEl!.removeAttribute("data-dragging");
        if (ghost) {
          ghost.remove();
          ghost = null;
        }
        clearTargets();
        dragCleanupRef.current = null;
      }
      function onUp() {
        const d = dragRef.current;
        cleanup();
        // Both state writes happen in the one handler, so React renders once:
        // the preview clears and the saved order takes its place in the same
        // paint. Clearing first on its own would snap the cards back to where
        // they started for a frame before the commit moved them again.
        setPreviewOrder(null);
        if (started && d && d.order) {
          commitOrder(d.order);
        }
        dragRef.current = null;
      }
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      dragCleanupRef.current = cleanup;
    }
    grid.addEventListener("pointerdown", onDown);
    return () => grid.removeEventListener("pointerdown", onDown);
  }, [commitOrder, locked]);

  const renderCard = (key: string, section: number) => {
    const card = cardByKey.get(key);
    if (!card) return null;
    return (
      <div
        key={key}
        data-tile-id={key}
        data-section={section}
        data-tile-title={card.title}
        // A card that renders nothing still got its wrapper - and its bottom
        // margin - leaving a phantom gap in the flow (WPS-175: ClaimTeamPanel
        // returns null when the team feature is off or has no slots, which put
        // a stray 16px above Homeowner & Property). We cannot know at wrap time
        // whether a child will render, so collapse the wrapper when the card
        // body comes out genuinely empty. Generic: every null-rendering card
        // benefits, and a card with real content is untouched.
        //
        // The wrapper itself stays a plain block: main moved the grab handle
        // INSIDE the card header (2b78c3548), which is why the old
        // `flex items-start gap-1` row is gone. Re-adding it here would undo
        // that layout change.
        className={cn(
          "group/reorder",
          "[&:has(>.cv-card-body:empty)]:hidden",
          // `itemClassName` carries the CSS-multi-column rules (a bottom
          // margin, break-inside-avoid). A packed band is a flex column whose
          // `gap` already does the spacing, so applying them there would
          // double the gap between cards.
          packed ? undefined : itemClassName,
        )}
      >
        {/* The grab affordance is rendered INSIDE the card's header (as the first
            item, pushing the card's own icon/title right) by <CardDragHandle />,
            which every card header renders and which shows the grip only when the
            card is inside this provider. The whole card is still draggable -
            pointerdown anywhere that isn't a button/link/input starts the drag. */}
        <div className="cv-card-body min-w-0 rounded-[8px]">
          {/* Pinned: skip the provider so no card renders a grip. Expressing the
              lock by withholding the provider rather than hiding the handles
              keeps one source of truth for whether a grip exists. */}
          {/* Pinned means "hold my layout still", so it withholds BOTH card
              controls - hiding a card rearranges the page just as much as
              dragging one does. Unpin to change either. */}
          {locked ? (
            card.node
          ) : (
            <CardDragHandleProvider
              title={card.title}
              onHide={
                hideable ? () => hideCard(card.key, card.title) : undefined
              }
            >
              {card.node}
            </CardDragHandleProvider>
          )}
        </div>
      </div>
    );
  };

  // Render bands in source order: a divider/static renders inline; the first card
  // of a band renders that band's whole card container (in effective order), and
  // the band's other cards are skipped (already emitted in the container).
  const emittedBands = new Set<number>();
  return (
    <div ref={gridRef} className={cn("cv-card-reorder", "space-y-6")}>
      {/* Clara Vale, Decision-First: the question "is something missing?" is
          asked while scanning the TOP of the page, so the answer belongs
          there too - not after the last card, where restoring a card meant
          scrolling the whole Overview to find the control (Law 4: every
          unnecessary click is a design failure). It sits directly under the
          "Pin my view" row, so everything that shapes this page is in one
          place. */}
      {hideable && hiddenCards.length > 0 ? (
        <HiddenCardsBar
          cards={hiddenCards.map((c) => ({ key: c.key, title: c.title }))}
          onRestore={restoreCard}
          onRestoreAll={restoreAll}
        />
      ) : null}
      {items.map((it) => {
        if (it.kind === "static") return <Fragment key={it.id}>{it.node}</Fragment>;
        if (it.kind === "divider") {
          // Drop a section heading once every card under it is hidden.
          const bandKeys = bands.get(it.section) ?? [];
          if (bandKeys.length === 0) return null;
          return <Fragment key={it.id}>{it.node}</Fragment>;
        }
        if (emittedBands.has(it.section)) return null;
        emittedBands.add(it.section);
        const bandKeys = bands.get(it.section) ?? [];
        if (bandKeys.length === 0) return null;
        if (packed) {
          // Explicit columns, dealt shortest-first, so a band's white space is
          // the difference between its two tallest stacks rather than whatever
          // CSS multi-column happened to leave.
          const columns = packIntoColumns(
            bandKeys,
            cardHeights,
            isWide ? packedColumns : 1,
            pinnedColumns,
          );
          return (
            <div
              key={`__band_${it.section}`}
              className="flex flex-col gap-6 md:flex-row md:items-start"
            >
              {columns.map((colKeys, i) => (
                <div
                  key={`__col_${i}`}
                  className="flex min-w-0 flex-1 flex-col gap-6"
                >
                  {colKeys.map((k) => renderCard(k, it.section))}
                </div>
              ))}
            </div>
          );
        }
        return (
          <div key={`__band_${it.section}`} className={className}>
            {bandKeys.map((k) => renderCard(k, it.section))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The way back from a hidden card.
 *
 * A hide that cannot be undone is a trap, so this is always present whenever
 * anything is hidden - never tucked behind a menu. Each card is named and
 * restorable in one click, because "put Claim Details back" is a single
 * decision and should cost a single action; "Restore all" appears only when
 * there is more than one, so it never adds noise to the common case.
 *
 * Styled as a quiet inline row rather than a boxed panel: it is a control for
 * the page, not content on it, and a dashed box at full card width read as
 * debug UI sitting among real cards.
 */
function HiddenCardsBar({
  cards,
  onRestore,
  onRestoreAll,
}: {
  cards: { key: string; title: string }[];
  onRestore: (key: string) => void;
  onRestoreAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="hidden-cards-bar"
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1"
    >
      <span className="flex items-center gap-1.5 pr-0.5 text-[11px] text-[var(--ink-3)]">
        <EyeOff className="h-3 w-3" />
        {t("detailCards.hiddenCount", "{{count}} card hidden", {
          count: cards.length,
        })}
      </span>
      {cards.map((card) => (
        <Button
          key={card.key}
          variant="ghost"
          size="xs"
          onClick={() => onRestore(card.key)}
          className="h-auto px-1.5 py-0.5 text-[11px] text-[var(--green)]"
        >
          <Eye className="mr-1 h-3 w-3" />
          {card.title}
        </Button>
      ))}
      {cards.length > 1 ? (
        <Button
          variant="ghost"
          size="xs"
          onClick={onRestoreAll}
          className="h-auto px-1.5 py-0.5 text-[11px] text-[var(--ink-3)]"
        >
          {t("detailCards.restoreAll", "Restore all")}
        </Button>
      ) : null}
    </div>
  );
}
